import { reactive } from 'vue';
import {
  addContractualPartyRepresentative,
  createContractManagementContractualParty,
  getContractManagementContractualParty,
  TemplateFieldType,
  updateContractManagementContractualParty,
  type CreateContractManagementContractualPartyData,
  type TemplateField,
  type TemplateSchema,
} from '@/api';
import type { ContractualParty } from '@/services/contractManagement/contractualPartyRow';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * A template's `legal_entity_*` / `natural_person_*` fields are filled from
 * contractual parties of the same kind; their sub-fields match the party
 * create schemas (a legal entity's `legal_name`, `street_address`, …).
 */
export type EntityFieldKind = 'legal_entity' | 'natural_person';

export type EntityRecord = ContractualParty;

export function isEntityFieldType(type: TemplateFieldType | undefined | null): boolean {
  return type === TemplateFieldType.LEGAL_ENTITY || type === TemplateFieldType.NATURAL_PERSON;
}

/**
 * Keys the party create endpoint needs to produce a valid record. If the
 * template doesn't include any of these in its `field.fields`, we can't ever
 * produce a valid record from the user's input — saving has to be disabled.
 * For natural persons the backend only requires one name part
 * (first/middle/last/artist); we require first + last here as a best-effort
 * gate for the common case, and the backend is the final authority.
 */
export const ENTITY_DIRECTORY_REQUIRED_KEYS: Record<EntityFieldKind, readonly string[]> = {
  legal_entity: ['legal_name'],
  natural_person: ['first_name', 'last_name'],
};

/**
 * Keys an entity slot's value carries beside the template's sub-fields: the
 * picked party (`uid`) and, for a legal entity, the representative it signs
 * through. Finalize links the party to the contract and makes it a signer;
 * the renderer ignores them.
 */
export const PARTY_LINK_KEYS = ['uid', 'representative_uid'] as const;

/**
 * What the signing provider needs on whoever signs — the person in a natural
 * person slot, a legal entity's representative. Read off the party record, so
 * a person typed into a signing slot needs them among the values saved with it.
 */
export const SIGNER_CONTACT_KEYS = ['first_name', 'last_name', 'email'] as const;

/**
 * Parties the drafting wizard has loaded, by uid — reactive, so the signing
 * readiness (`signerGaps`) updates the moment a slot's party (or the person
 * signing for it) is loaded or fixed.
 */
export const partyCache = reactive(new Map<string, ContractualParty>());

export const EntityFieldService = {
  /** A party from the cache, else loaded into it; `fresh` re-reads it after an edit. */
  async getParty(uid: string, fresh = false): Promise<ContractualParty> {
    const cached = partyCache.get(uid);
    if (cached && !fresh) return cached;
    try {
      const { data } = await getContractManagementContractualParty({
        path: { uid },
        throwOnError: true,
      });
      partyCache.set(uid, data);
      return data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load the contractual party.'));
    }
  },

  /** Make `personUid` a representative of the legal entity (idempotent) and re-read it. */
  async addRepresentative(legalEntityUid: string, personUid: string): Promise<ContractualParty> {
    try {
      await addContractualPartyRepresentative({
        query: { legal_entity_uid: legalEntityUid, representative_uid: personUid },
        throwOnError: true,
      });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to link the representative.'));
    }
    return EntityFieldService.getParty(legalEntityUid, true);
  },

  /** Save what the user typed as a new party of `kind`; blanks are sent as null. */
  async createParty(
    kind: EntityFieldKind,
    values: Record<string, unknown>
  ): Promise<ContractualParty> {
    const fields = blanksAsNull(values);
    try {
      const { data } = await createContractManagementContractualParty({
        // The template's sub-fields are the create schema's, so the typed bag is the body.
        body: { ...fields, kind } as CreateContractManagementContractualPartyData['body'],
        throwOnError: true,
      });
      partyCache.set(data.uid, data);
      return data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, `Failed to save the ${entityKindLabel(kind)}.`));
    }
  },

  /** Write `values` onto an existing party (a partial update; blanks clear) and re-cache it. */
  async updateParty(
    uid: string,
    kind: EntityFieldKind,
    values: Record<string, unknown>
  ): Promise<ContractualParty> {
    try {
      const { data } = await updateContractManagementContractualParty({
        path: { uid },
        body: { ...blanksAsNull(values), kind },
        throwOnError: true,
      });
      partyCache.set(data.uid, data);
      return data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, `Failed to update the ${entityKindLabel(kind)}.`));
    }
  },
};

function blanksAsNull(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() || null : value,
    ])
  );
}

/** The keys of `values` that differ from `party` (blank and missing count as equal). */
export function changedPartyKeys(
  values: Record<string, unknown>,
  party: ContractualParty
): string[] {
  const record = party as Record<string, unknown>;
  const norm = (value: unknown): unknown =>
    typeof value === 'string' ? value.trim() || null : (value ?? null);
  return Object.keys(slotPartyValues(values)).filter(
    key => JSON.stringify(norm(values[key])) !== JSON.stringify(norm(record[key]))
  );
}

/**
 * The template's sub-fields filled from a party: only the keys the template
 * asks for plus the party's `uid` link, so server-only data (timestamps, the
 * computed `name`, `country_name`, representatives) never reaches the renderer.
 */
export function entityToFormValue(
  entity: EntityRecord,
  keys: readonly string[]
): Record<string, unknown> {
  const record = entity as Record<string, unknown>;
  return { ...Object.fromEntries(keys.map(key => [key, record[key] ?? ''])), uid: entity.uid };
}

function text(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value.trim() : '';
}

/** The values a slot holds beside its party link — what saving it as a party sends. */
export function slotPartyValues(value: Record<string, unknown>): Record<string, unknown> {
  const values = { ...value };
  for (const key of PARTY_LINK_KEYS) delete values[key];
  return values;
}

/** True when a slot has anything typed or picked in it. */
export function slotIsFilled(value: Record<string, unknown>): boolean {
  return Object.values(value).some(entry =>
    typeof entry === 'string' ? entry.trim().length > 0 : entry !== null && entry !== undefined
  );
}

/** "an email", "a first name and an email" — what a signer record still lacks. */
export function missingSignerContact(record: Record<string, unknown>): string[] {
  const names: Record<(typeof SIGNER_CONTACT_KEYS)[number], string> = {
    first_name: 'a first name',
    last_name: 'a last name',
    email: 'an email',
  };
  return SIGNER_CONTACT_KEYS.filter(key => !text(record, key)).map(key => names[key]);
}

export function joinAnd(parts: string[]): string {
  return parts.length <= 1
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}

/**
 * What still stops one signing slot from signing, or null when it is ready.
 * A slot typed by hand counts: it is saved as a party on send
 * (`ensureSignerParties`), so it needs what saving and signing need. A record
 * not loaded yet is given the benefit of the doubt — the backend has the last word.
 */
export function slotSignerGap(
  kind: EntityFieldKind,
  field: TemplateField,
  value: Record<string, unknown>
): string | null {
  const uid = typeof value.uid === 'string' ? value.uid : null;
  if (!uid && !slotIsFilled(value)) {
    // An optional signing slot left empty signs nothing (its tag renders nothing).
    return field.required ? `pick or enter the ${entityKindLabel(kind)} who signs` : null;
  }
  if (!uid) {
    const unsaveable = ENTITY_DIRECTORY_REQUIRED_KEYS[kind].filter(key => !text(value, key));
    if (unsaveable.length > 0) return `add ${joinAnd(unsaveable.map(humanize))}`;
  }

  if (kind === 'natural_person') {
    const record = uid ? partyCache.get(uid) : value;
    const missing = record ? missingSignerContact(record) : [];
    return missing.length > 0 ? `add ${joinAnd(missing)} to sign` : null;
  }

  const representativeUid =
    typeof value.representative_uid === 'string' ? value.representative_uid : null;
  if (!representativeUid) return 'pick the person who signs for it';
  const person = partyCache.get(representativeUid);
  const missing = person ? missingSignerContact(person) : [];
  return missing.length > 0 ? `the person signing needs ${joinAnd(missing)}` : null;
}

function humanize(key: string): string {
  return key.replaceAll('_', ' ');
}

/**
 * Why the document can't be sent for signing yet, one line per signing slot
 * that isn't ready. Empty means ready.
 */
export function signerGaps(
  schema: TemplateSchema,
  formData: Record<string, unknown>,
  label: (slot: string) => string
): string[] {
  return Object.entries(schema.fields ?? {}).flatMap(([slot, field]) => {
    if (!field.signer || !isEntityFieldType(field.type)) return [];
    const kind: EntityFieldKind =
      field.type === TemplateFieldType.NATURAL_PERSON ? 'natural_person' : 'legal_entity';
    const gap = slotSignerGap(kind, field, (formData[slot] ?? {}) as Record<string, unknown>);
    return gap ? [`${label(slot)}: ${gap}`] : [];
  });
}

export interface SignerLine {
  slot: string;
  /** The person who signs, e.g. "Jane Doe" — or "Acme GmbH: Jane Doe" for a company. */
  label: string;
}

/**
 * Who signs, in the template's slot order — the backend's unless
 * `signer_order` says otherwise: one line per filled signing slot.
 */
export function signerOrder(
  schema: TemplateSchema,
  formData: Record<string, unknown>
): SignerLine[] {
  return Object.entries(schema.fields ?? {}).flatMap(([slot, field]) => {
    if (!field.signer || !isEntityFieldType(field.type)) return [];
    const value = (formData[slot] ?? {}) as Record<string, unknown>;
    if (!slotIsFilled(slotPartyValues(value)) && typeof value.uid !== 'string') return [];
    const party = typeof value.uid === 'string' ? partyCache.get(value.uid) : undefined;
    const person =
      typeof value.representative_uid === 'string'
        ? partyCache.get(value.representative_uid)
        : undefined;
    const name = describeEntity((party as Record<string, unknown> | undefined) ?? value);
    return [{ slot, label: person ? `${name}: ${person.name}` : name }];
  });
}

/**
 * Load the parties (and the people signing for companies) of every signing
 * slot into the cache — a slot's own field loads them only while its section
 * is on screen, and the signer list and readiness read the cache.
 */
export async function loadSignerParties(
  schema: TemplateSchema,
  formData: Record<string, unknown>
): Promise<void> {
  const uids = Object.entries(schema.fields ?? {}).flatMap(([slot, field]) => {
    if (!field.signer) return [];
    const value = (formData[slot] ?? {}) as Record<string, unknown>;
    return [value.uid, value.representative_uid].filter(
      (uid): uid is string => typeof uid === 'string'
    );
  });
  await Promise.allSettled(uids.map(uid => EntityFieldService.getParty(uid)));
}

/**
 * A `signer_order` the backend accepts: it must name every signing slot, so
 * the ones left out of `order` (optional slots left empty, which sign
 * nothing) follow in the template's order.
 */
export function completeSignerOrder(schema: TemplateSchema, order: string[]): string[] {
  const slots = Object.entries(schema.fields ?? {})
    .filter(([, field]) => field.signer)
    .map(([slot]) => slot);
  return [...order, ...slots.filter(slot => !order.includes(slot))];
}

/**
 * Right before sending: every signing slot typed by hand becomes a party (the
 * provider signs parties, not form values), and a legal entity's chosen
 * signer becomes its representative. Each new link is handed to `store` the
 * moment its party exists, so a failure on a later slot can't leave an earlier
 * party unlinked — and a retry can't create it twice.
 */
export async function ensureSignerParties(
  schema: TemplateSchema,
  formData: Record<string, unknown>,
  store: (slot: string, value: Record<string, unknown>) => void
): Promise<void> {
  for (const [slot, field] of Object.entries(schema.fields ?? {})) {
    if (!field.signer || !isEntityFieldType(field.type)) continue;
    const value = (formData[slot] ?? {}) as Record<string, unknown>;
    if (!slotIsFilled(value)) continue;
    const kind: EntityFieldKind =
      field.type === TemplateFieldType.NATURAL_PERSON ? 'natural_person' : 'legal_entity';

    let uid = typeof value.uid === 'string' ? value.uid : null;
    if (!uid) {
      const created = await EntityFieldService.createParty(kind, slotPartyValues(value));
      uid = created.uid;
      store(slot, { ...value, uid });
    }
    const representativeUid =
      typeof value.representative_uid === 'string' ? value.representative_uid : null;
    if (kind === 'legal_entity' && representativeUid) {
      const party = partyCache.get(uid) ?? (await EntityFieldService.getParty(uid));
      const linked =
        party.kind === 'legal_entity' &&
        (party.representatives ?? []).some(person => person.uid === representativeUid);
      if (!linked) await EntityFieldService.addRepresentative(uid, representativeUid);
    }
  }
}

export function entityKindLabel(kind: EntityFieldKind): string {
  return kind === 'legal_entity' ? 'legal entity' : 'natural person';
}

export function describeEntity(entity: Record<string, unknown>): string {
  const str = (key: string): string => (typeof entity[key] === 'string' ? entity[key].trim() : '');
  // A party carries a computed `name`; typed values have a legal entity's
  // `legal_name`, or a person's structured parts, falling back to the artist name.
  const name = str('name') || str('legal_name');
  if (name) return name;
  const combined = [
    str('title'),
    str('first_name'),
    str('middle_name'),
    str('last_name'),
    str('suffix'),
  ]
    .filter(Boolean)
    .join(' ');
  if (combined) return combined;
  const artist = str('artist_name');
  if (artist) return artist;
  const email = str('email');
  if (email) return email;
  return 'Unnamed';
}
